import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardList, FileCheck, Users, Gift, Banknote, Download, LogOut, Menu, X, ShieldCheck, Headphones } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, id: "admin-nav-dashboard", end: true },
  { to: "/admin/tasks", label: "Tasks", icon: ClipboardList, id: "admin-nav-tasks" },
  { to: "/admin/submissions", label: "Submissions", icon: FileCheck, id: "admin-nav-submissions" },
  { to: "/admin/users", label: "Users", icon: Users, id: "admin-nav-users" },
  { to: "/admin/rewards", label: "Rewards", icon: Gift, id: "admin-nav-rewards" },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: Banknote, id: "admin-nav-withdrawals" },
  { to: "/admin/exports", label: "Exports & Settings", icon: Download, id: "admin-nav-exports" },
  { to: "/admin/support", label: "Support", icon: Headphones, id: "admin-nav-support" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openTickets, setOpenTickets] = useState(0);

  useEffect(() => {
    const load = () => api.get("/admin/support?status=open").then((r) => setOpenTickets(r.data.length)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("support-updated", load);
    return () => { clearInterval(t); window.removeEventListener("support-updated", load); };
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const navContent = (
    <nav className="space-y-1">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.id} onClick={() => setOpen(false)}
          className={({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}>
          <n.icon className="w-[18px] h-[18px]" /> {n.label}
          {n.id === "admin-nav-support" && openTickets > 0 && (
            <span data-testid="support-open-badge" className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{openTickets}</span>
          )}
        </NavLink>
      ))}
      <button data-testid="admin-logout-btn" onClick={doLogout} className="w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-medium text-red-400 hover:bg-slate-800 transition-colors">
        <LogOut className="w-[18px] h-[18px]" /> Logout
      </button>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-100 lg:flex">
      <aside className="hidden lg:flex lg:flex-col w-64 shrink-0 bg-slate-900 min-h-screen p-4 sticky top-0 h-screen">
        <div className="flex items-center gap-2.5 px-2 py-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <span className="block font-heading font-bold text-white text-sm">Equinox 4 Review</span>
            <span className="block text-[10px] font-medium text-slate-400 tracking-wider">ADMIN PANEL</span>
          </div>
        </div>
        <div className="mt-6 flex-1">{navContent}</div>
        <p data-testid="admin-header-email" className="px-2 text-xs text-slate-500 truncate">{user?.email}</p>
      </aside>

      <div className="lg:hidden sticky top-0 z-40 bg-slate-900 text-white">
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            <span className="font-heading font-bold text-sm">Admin Panel</span>
          </div>
          <button data-testid="admin-mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-slate-800" aria-label="Menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {open && <div className="px-4 pb-4" data-testid="admin-mobile-menu">{navContent}</div>}
      </div>

      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8">
        <Outlet />
      </main>
    </div>
  );
}
